/** @jsxImportSource @emotion/react */

import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { FC } from 'react'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Table, { TableProps } from 'components/Table'
import { cmsRoutes, useLink } from 'routes/cmsRoutes'
import { FIND_ALL_PRODUCT } from 'queries/products'
import Loader from 'components/Loader'
import { convertToDollars, priceFormatter } from 'utils/priceFormatter'
import { pluralize } from 'utils/pluralize'
import {
  findAllProduct,
  findAllProductVariables,
  findAllProduct_findAllProduct_items,
} from '__generatedTypes__/findAllProduct'
import { OrderDirection } from '__generatedTypes__/globalTypes'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import * as classes from './styles'

type Product = findAllProduct_findAllProduct_items

const FREE_MEMBERSHIP_PRICE = 0

type Props = {
  isExtension: boolean
  platform: Platform
}

export const List: FC<Props> = ({ isExtension, platform }) => {
  const { getLink } = useLink()

  const { data, loading } = useQuery<findAllProduct, findAllProductVariables>(FIND_ALL_PRODUCT, {
    fetchPolicy: 'network-only',
    variables: {
      query: {
        order: {
          price: OrderDirection.ASC,
        },
        where: {
          isExtension,
          platform: {
            id: platform.id,
          },
        },
      },
    },
  })

  if (loading) {
    return <Loader />
  }

  const productColumns: TableProps<Product>['columns'] = [
    {
      title: 'Stripe Name',
      renderCell: (membership) => <Typography variant="body2">{membership.name}</Typography>,
      flex: '2',
    },
    {
      title: 'Status',
      renderCell: (membership) => <Typography variant="body2">{membership.status}</Typography>,
      flex: '1',
    },
    {
      title: 'Duration',
      renderCell: ({ duration, price, isSubscription }) => {
        if (isSubscription) {
          return (
            <Typography variant="body2">
              {price === FREE_MEMBERSHIP_PRICE ? '-' : duration === '30' ? 'Month' : 'Year'}
            </Typography>
          )
        }

        return (
          <Typography variant="body2">
            {duration} {price !== FREE_MEMBERSHIP_PRICE && pluralize(parseInt(duration), 'Day', 'Days')}
          </Typography>
        )
      },
      flex: '1',
    },
    {
      title: 'Price',
      renderCell: ({ price }) => (
        <Typography variant="body2">
          {price === FREE_MEMBERSHIP_PRICE ? 'Free' : priceFormatter.format(convertToDollars(price))}
        </Typography>
      ),
      flex: '1',
    },
    {
      title: 'Active',
      renderCell: ({ active }) => <Typography variant="body2">{active}</Typography>,
      flex: '1',
    },
    {
      renderCell: (membership) =>
        membership.price === FREE_MEMBERSHIP_PRICE ? (
          <div />
        ) : (
          <Link
            to={getLink(isExtension ? cmsRoutes.PRODUCTS_EXTENSION_EDIT : cmsRoutes.PRODUCTS_MEMBERSHIP_EDIT, {
              id: membership.stripeProductId,
            })}
          >
            <IconButton size="small" css={classes.link}>
              <EditIcon />
            </IconButton>
          </Link>
        ),
      flex: '0 1 44px',
    },
  ]

  const items = data?.findAllProduct?.items || []

  return <Table columns={productColumns} items={items} />
}
